import { Fragment, useEffect, useContext, useMemo } from "react";
import { UserContext } from "../../../../../../context/UserContext";
import { FlowHandlersContext } from "../../../../Menu/FlowContainer";
import { getAdjustedDate } from "../../../../../../legacyComponents/util/helperFunctions";
import { getCurrency } from "../../../../../../legacyComponents/util/formating";
import { options } from "../util";
import { Stack, Typography, Box, Link } from "@mui/material";
import { styled } from "@mui/system";
import { Field } from "formik";
import { FormContainer, NavButtons } from "../../../../Menu/Flow";
import { AgeAllowanceTable, FamilyAllowanceTable } from "../components";
import { MAX_BENEFIT_AGE_LIST } from "../../../../../../legacyComponents/util/api/apis/employer/PermissibleClass/constants";
import { ListContainer } from "../../../../../../legacyComponents/componentsV2/Flow";
import CheckboxField from "../../../../../../components/CheckboxField";
import { TextField } from "../../../../../../legacyComponents/componentsV2/FlowV2/Fields";
import employerFlowStyles from "../../styles";

const LearnMoreButton = styled(Link)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "14px 20px",
  borderRadius: "14px",
  border: "1px solid #6C63FF",
  backgroundColor: "rgba(108, 99, 255, .1)",
  transition: "all .2s ease",
  color: "#6C63FF",
  fontWeight: 500,
  fontSize: "14px",
  textDecoration: "none",
  width: "fit-content",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.8,
    textDecoration: "none",
  },
});
const LearnMoreLink = styled(Link)({
  color: "#6C63FF",
  fontWeight: 500,
  fontSize: "14px",
  textDecoration: "none",
  whiteSpace: "nowrap",
  cursor: "pointer",
  "&:hover": { textDecoration: "underline" },
});

/**
 * How allocations, carry-forward and the plan year work. Same message box
 * style as the "Impact on Reimbursement Method" banner on Eligible Expenses.
 */
const ACCRUAL_POINTS = [
  [
    "Allocations",
    "Made monthly, with unused funds accumulating throughout the plan year.",
  ],
  [
    "Reimbursements",
    "Expenses are reimbursed up to available funds. Unreimbursed amounts carry forward and are reimbursed as future allocations become available.",
  ],
  [
    "Plan Year Limits",
    "Unused allowance funds do not carry over to the next plan year.",
  ],
];

function AccrualNote({ href }) {
  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        backgroundColor: "#f8f9fc",
        border: "1px solid #ACBEE8",
        borderRadius: "8px",
      }}
    >
      <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
        How the Allowance Works
      </Typography>
      <Box component="ul" sx={{ margin: 0, paddingLeft: "20px" }}>
        {ACCRUAL_POINTS.map(([title, text]) => (
          <Typography
            component="li"
            key={title}
            sx={{
              fontSize: "14px",
              color: "#465578",
              marginBottom: "4px",
              lineHeight: 1.6,
            }}
          >
            <strong>{title}:</strong> {text}
          </Typography>
        ))}
      </Box>
      {href && (
        <Box sx={{ marginTop: "8px" }}>
          <LearnMoreLink href={href} target="_blank" rel="noopener noreferrer">
            Learn More →
          </LearnMoreLink>
        </Box>
      )}
    </Box>
  );
}
export default function Allowance(props) {
  const { getEmployerPlan, getPermissibleClass } = useContext(UserContext);
  const flowHandlers = useContext(FlowHandlersContext);
  const {
    mode,
    exitUrl,
    step,
    formik,
    displayOnly,
    permissibleClassId,
    systemVariables,
  } = props;

  let employerPlan = null;
  let permissibleClass = null;
  if (permissibleClassId) {
    permissibleClass = getPermissibleClass(permissibleClassId);
    const employerPlanId = permissibleClass?.employerPlanId;
    employerPlan = getEmployerPlan(employerPlanId);
  }
  const planType = employerPlan?.type || "";

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    submitCount,
  } = formik;
  const shouldShowErrors = submitCount > 0;

  let selfLabel = "Self Amount";
  if (values?.maxBenefit?.SELF) {
    selfLabel = "Monthly Amount";
  }

  let annualSelfAmountMax = 0;
  let annualFamilyAmountMax = 0;
  let monthlySelfAmountMax = Infinity;
  let monthlyFamilyAmountMax = Infinity;

  let monthlySelfDisplayMax = "";
  let monthlyFamilyDisplayMax = "";
  let annualSelfDisplayMax = "";
  let annualFamilyDisplayMax = "";
  let v2 = false;

  if (permissibleClass) {
    v2 = permissibleClass?.v2;
  } else {
    v2 = true;
  }

  let inputOptions = options.maxBenefits?.[planType] || [];

  const columns = useMemo(() => {
    return v2
      ? ["selfAmount", "spouseAmount", "childAmount"]
      : ["selfAmount", "spouseAmount", "childAmount", "allAmount"];
  }, [v2]);

  // const startDate = getAdjustedDate({ date: values?.startDate?.value });
  // const planYear = startDate?.getFullYear();

  /**
   * Only QSEHRA and EBHRA carry a statutory cap. ICHRA has none, so applying
   * one there would block a legitimately large allowance.
   */
  if (planType === "QSEHRA") {
    annualSelfAmountMax = systemVariables.MAX_CONTRIBUTION_ANNUAL.single || 0;
    annualFamilyAmountMax = systemVariables.MAX_CONTRIBUTION_ANNUAL.family || 0;
  } else if (planType === "EBHRA") {
    annualSelfAmountMax = systemVariables.MAX_CONTRIBUTION_ANNUAL.single || 0;
  }

  if (annualSelfAmountMax > 0) {
    monthlySelfAmountMax = Math.floor(annualSelfAmountMax / 12.0);
    monthlySelfDisplayMax = getCurrency({
      amount: monthlySelfAmountMax,
      actions: ["divide"],
    });
    annualSelfDisplayMax = getCurrency({
      amount: annualSelfAmountMax,
      actions: ["divide"],
    });
  }

  if (annualFamilyAmountMax > 0) {
    monthlyFamilyAmountMax = Math.floor(annualFamilyAmountMax / 12.0);
    monthlyFamilyDisplayMax = getCurrency({
      amount: monthlyFamilyAmountMax,
      actions: ["divide"],
    });
    annualFamilyDisplayMax = getCurrency({
      amount: annualFamilyAmountMax,
      actions: ["divide"],
    });
  }

  // ── Learn More link ──
  const learnMoreHref = (() => {
    const maxBenefit = values?.maxBenefit || {};
    const hasBenefitSelection = Object.values(maxBenefit).some(Boolean);
    if (!hasBenefitSelection) return null;
    if (maxBenefit.FAMILY_AGE)
      return "https://salusion.com/learning-center/setting-the-allowance/#by-age-family-size";
    if (maxBenefit.AGE)
      return "https://salusion.com/learning-center/setting-the-allowance/#by-age";
    if (maxBenefit.FAMILY)
      return "https://salusion.com/learning-center/setting-the-allowance/#by-family-size";
    if (maxBenefit.SELF)
      return "https://salusion.com/learning-center/setting-the-allowance/#one-size-fits-all";
    return null;
  })();

  useEffect(() => {
    if (submitCount > 0) {
      formik.resetForm({ values: formik.values });
    }
  }, [submitCount, formik.resetForm, formik.values]);

  // Track maxBenefit_required for validation
  useEffect(() => {
    const maxBenefit = values?.maxBenefit || {};
    const hasSelection = Object.values(maxBenefit).some(Boolean);
    const nextRequiredValue = hasSelection ? "yes" : "";
    if ((values?.maxBenefit_required || "") !== nextRequiredValue) {
      setFieldValue("maxBenefit_required", nextRequiredValue);
    }
  }, [values?.maxBenefit, values?.maxBenefit_required, setFieldValue]);

  // Mark fields as touched when we want to show errors
  useEffect(() => {
    if (shouldShowErrors) {
      if (errors?.maxBenefit) {
        setFieldTouched("maxBenefit", true, false);
      }
      if (errors?.age_qualify) {
        setFieldTouched("age_qualify", true, false);
      }
    }
  }, [
    shouldShowErrors,
    errors?.maxBenefit,
    errors?.age_qualify,
    setFieldTouched,
  ]);

  useEffect(() => {
    if (values.maxBenefit?.AGE) {
      let selfAmounts = [];
      let amountError = false;
      let maxExceeded = false;
      let previousValue = 0;
      let columnErrors = {};

      for (
        let i = MAX_BENEFIT_AGE_LIST.MINIMUM_AGE;
        i <= MAX_BENEFIT_AGE_LIST.MAXIMUM_AGE;
        i++
      ) {
        let selfAmount = values["selfAmount" + i];
        selfAmounts.push(selfAmount);
        if (previousValue > selfAmount) {
          columnErrors["row" + i] = true;
          amountError = true;
        }
        /**
         * Grid values are ANNUAL CENTS — see the /12 in AgeAllowanceTable,
         * which is what turns 480000 into the $400.00 shown per month. So the
         * comparison is against the ANNUAL max, not the monthly one, and no
         * conversion is needed. The flat-amount path above is different again:
         * monthlySelfAmount is dollars and does need converting.
         *
         * Guarded on > 0 rather than < Infinity: the annual maxes default to
         * 0 when the plan type carries no statutory cap.
         */
        if (annualSelfAmountMax > 0) {
          if ((Number(selfAmount) || 0) > annualSelfAmountMax) {
            columnErrors["row" + i] = true;
            maxExceeded = true;
          }
        }
        previousValue = selfAmount;
      }
      const validAmounts = selfAmounts.filter(
        (amt) => amt != null && amt !== "",
      );
      if (validAmounts.length > 0) {
        const minAmount = Math.min(...validAmounts);
        const maxAmount = Math.max(...validAmounts);
        if (maxAmount > minAmount * 3) {
          for (
            let i = MAX_BENEFIT_AGE_LIST.MINIMUM_AGE;
            i <= MAX_BENEFIT_AGE_LIST.MAXIMUM_AGE;
            i++
          ) {
            if (values["selfAmount" + i] === maxAmount) {
              columnErrors["row" + i] = true;
              amountError = true;
              break;
            }
          }
        }
      }
      let sameAmountArr = [...selfAmounts];
      sameAmountArr.sort((a, b) => {
        return a - b;
      });
      let sameAmount =
        sameAmountArr[0] === sameAmountArr[sameAmountArr.length - 1];
      if (selfAmounts[0]) {
        if (sameAmount || !amountError) {
          setFieldValue("age_qualify", "Yes");
        } else {
          setFieldValue("age_qualify", "No");
        }
      } else {
        setFieldValue("age_qualify", "No");
      }
      setFieldValue("columnErrors", columnErrors);
      setFieldValue("ageAmountExceedsMax", maxExceeded ? "No" : "Yes");
    }
  }, [
    values.maxBenefit,
    monthlySelfAmountMax,
    Array.from(
      {
        length:
          MAX_BENEFIT_AGE_LIST.MAXIMUM_AGE -
          MAX_BENEFIT_AGE_LIST.MINIMUM_AGE +
          1,
      },
      (_, i) => {
        const age = i + MAX_BENEFIT_AGE_LIST.MINIMUM_AGE;
        return values["selfAmount" + age] ?? "";
      },
    ).join(","),
    setFieldValue,
  ]);

  useEffect(() => {
    if (values.maxBenefit?.FAMILY_AGE) {
      let validated = "Yes";
      let maxExceeded = false;
      let columnErrors = {};
      columns.forEach((key) => {
        let amounts = [];
        let amountError = false;
        let previousValue = 0;
        /**
         * selfAmount is the employee-only column and takes the self-only cap.
         * The others include dependents and take the family cap.
         *
         * ASSUMES those columns hold TOTALS (self + dependents), matching the
         * V1 labels "Self and Spouse Amount" etc. If they are increments the
         * comparison should be self + increment instead.
         */
        // Only the employee-only column has a cap of its own. The dependent
        // columns are checked as a ROW TOTAL below — self + first dependent
        // against the family max — because that is what an employee taking
        // family coverage actually receives.
        const columnMax = key === "selfAmount" ? annualSelfAmountMax : 0;

        for (
          let i = MAX_BENEFIT_AGE_LIST.MINIMUM_AGE;
          i <= MAX_BENEFIT_AGE_LIST.MAXIMUM_AGE;
          i++
        ) {
          let amount = values[key + i];
          amounts.push(amount);
          if (previousValue > amount) {
            amountError = true;
            columnErrors[key + "_row" + i] = true;
          }
          // Annual cents against the annual max — see the note in the AGE
          // effect.
          if (columnMax > 0) {
            if ((Number(amount) || 0) > columnMax) {
              columnErrors[key + "_row" + i] = true;
              maxExceeded = true;
            }
          }
          previousValue = amount;
        }
        const validAmounts = amounts.filter((amt) => amt != null && amt !== "");
        // 3x applies to the employee (self) column only. Spouse and child
        // columns may start at $0. Same rule as allowanceChecks.js.
        if (key === "selfAmount" && validAmounts.length > 0) {
          const minAmount = Math.min(...validAmounts);
          const maxAmount = Math.max(...validAmounts);
          if (maxAmount > minAmount * 3) {
            for (
              let i = MAX_BENEFIT_AGE_LIST.MINIMUM_AGE;
              i <= MAX_BENEFIT_AGE_LIST.MAXIMUM_AGE;
              i++
            ) {
              if (values[key + i] === maxAmount) {
                columnErrors[key + "_row" + i] = true;
                amountError = true;
                break;
              }
            }
          }
        }

        let sameAmountArr = [...amounts];
        sameAmountArr.sort((a, b) => {
          return a - b;
        });
        let sameAmount =
          sameAmountArr[0] === sameAmountArr[sameAmountArr.length - 1];

        if (amounts?.length > 0) {
          if (!sameAmount && amountError) {
            validated = "No";
          }
        } else {
          validated = "No";
        }
      });

      /**
       * Per age: self + first dependent cannot exceed the IRS family maximum.
       * "First dependent" is a POSITION, not a relationship — the second
       * column pays for whichever dependent comes first, spouse or child. So
       * only that column enters the sum; the third column is for each
       * additional dependent beyond it.
       */
      if (annualFamilyAmountMax > 0) {
        for (
          let i = MAX_BENEFIT_AGE_LIST.MINIMUM_AGE;
          i <= MAX_BENEFIT_AGE_LIST.MAXIMUM_AGE;
          i++
        ) {
          const self = Number(values["selfAmount" + i]) || 0;
          const firstDependent = Number(values["spouseAmount" + i]) || 0;

          if (self + firstDependent > annualFamilyAmountMax) {
            // Only the dependent cell. The self amount has its own check
            // against the self-only cap and is flagged there when it is the
            // problem; here it is legal on its own and the dependent amount
            // is what carries the pair over.
            columnErrors["spouseAmount_row" + i] = true;
            maxExceeded = true;
          }
        }
      }
      setFieldValue("age_qualify", validated);
      setFieldValue("columnErrors", columnErrors);
      setFieldValue("ageAmountExceedsMax", maxExceeded ? "No" : "Yes");
    }
  }, [
    values.maxBenefit,
    monthlySelfAmountMax,
    monthlyFamilyAmountMax,
    columns.join(","),
    // NOT spread: the spread applied to .join() split the string into
    // individual characters, so the array's length tracked the digit
    // count of the grid values and changed whenever their magnitude did.
    columns
      .flatMap((key) =>
        Array.from(
          {
            length:
              MAX_BENEFIT_AGE_LIST.MAXIMUM_AGE -
              MAX_BENEFIT_AGE_LIST.MINIMUM_AGE +
              1,
          },
          (_, i) => {
            const age = i + MAX_BENEFIT_AGE_LIST.MINIMUM_AGE;
            return values[key + age] ?? "";
          },
        ),
      )
      .join(","),
    setFieldValue,
  ]);

  // Neither grid is showing, so a flag left behind by one is stale. A stale
  // "No" would block submit with nothing visible — the message only renders
  // inside the grid blocks.
  useEffect(() => {
    if (!values.maxBenefit?.AGE && !values.maxBenefit?.FAMILY_AGE) {
      setFieldValue("ageAmountExceedsMax", "Yes");
    }
  }, [values.maxBenefit?.AGE, values.maxBenefit?.FAMILY_AGE, setFieldValue]);

  // Validate self amount against max
  useEffect(() => {
    if (monthlySelfAmountMax < Infinity) {
      const selfAmountCents = Math.round(
        (Number(values.monthlySelfAmount) || 0) * 100,
      );
      if (selfAmountCents > monthlySelfAmountMax) {
        setFieldValue("selfAmountExceedsMax", "No");
      } else {
        setFieldValue("selfAmountExceedsMax", "Yes");
      }
    } else {
      setFieldValue("selfAmountExceedsMax", "Yes");
    }
  }, [values.monthlySelfAmount, monthlySelfAmountMax, setFieldValue]);

  // Validate family amount against max
  useEffect(() => {
    if (values?.maxBenefit?.FAMILY && monthlyFamilyAmountMax < Infinity) {
      const selfAmountCents = Math.round(
        (Number(values.monthlySelfAmount) || 0) * 100,
      );
      const firstDependentCents = Math.round(
        (Number(values.monthlyFirstDependentAmount) || 0) * 100,
      );
      const total = selfAmountCents + firstDependentCents;
      if (total > monthlyFamilyAmountMax) {
        setFieldValue("familyAmountExceedsMax", "No");
      } else {
        setFieldValue("familyAmountExceedsMax", "Yes");
      }
    } else {
      setFieldValue("familyAmountExceedsMax", "Yes");
    }
  }, [
    values?.maxBenefit?.FAMILY,
    values.monthlySelfAmount,
    values.monthlyFirstDependentAmount,
    monthlyFamilyAmountMax,
    setFieldValue,
  ]);

  /**
   * The amount checks live in effects and publish their result to hidden
   * formik fields. Nothing consults them on the way out, so Next has to.
   *
   * Belt and braces with the Yup schema: this stops the click, the schema
   * stops a submit that arrives by any other route.
   */
  const hasAmountMaxErrors = () =>
    values.selfAmountExceedsMax === "No" ||
    values.familyAmountExceedsMax === "No" ||
    values.ageAmountExceedsMax === "No";

  async function handleNext(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const validationErrors = await formik.validateForm();
    console.log("next", {
      validationErrors,
      self: values.selfAmountExceedsMax,
      family: values.familyAmountExceedsMax,
      age: values.ageAmountExceedsMax,
    });
    if (Object.keys(validationErrors).length > 0 || hasAmountMaxErrors()) {
      formik.setTouched(
        Object.keys(validationErrors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
        true,
      );
      return false;
    }

    await formik.submitForm();

    /**
     * NavButtons advances the flow itself when no onNextStep is supplied.
     * Overriding it to run the amount checks means this has to do the
     * advancing too — without this the form saves and the screen sits there.
     */
    if (flowHandlers?.onNext) {
      await flowHandlers.onNext(formik.values);
    }
    return false;
  }
  // Render function for V2 Manual Entry
  const renderV2ManualEntry = () => (
    <Stack spacing="30px" height="100%">
      <FormContainer>
        <AccrualNote href={learnMoreHref} />
        <Typography variant="h2" sx={employerFlowStyles.h2}>
          Monthly Allowance
        </Typography>
        <Typography>Choose how to set the allowance amount.</Typography>
        <Stack spacing={2} sx={{ marginTop: "20px" }}>
          {/* Radio buttons for max benefit type */}
          <CheckboxField
            formik={formik}
            type="radio"
            name="maxBenefit"
            options={inputOptions}
            displayKey="display"
            valueKey="value"
            disabled={displayOnly}
            showError={shouldShowErrors}
            validation={({ value }) => {
              const maxBenefit = value || {};
              const hasSelection = Object.values(maxBenefit).some(Boolean);
              if (!hasSelection) {
                return "Please select a benefit type";
              }
              return null;
            }}
          />

          <Field name="maxBenefit_required">
            {({ field }) => <input {...field} type="hidden" />}
          </Field>
          {errors.maxBenefit_required && shouldShowErrors && (
            <div className="error-message-fields">
              {errors.maxBenefit_required}
            </div>
          )}

          {/* Single Self Amount */}
          {values.maxBenefit?.SELF && (
            <Stack spacing="0px">
              <TextField
                formik={formik}
                type="number"
                name="monthlySelfAmount"
                label={selfLabel}
                disabled={displayOnly}
                validation="string"
              />
              {values.selfAmountExceedsMax === "No" && (
                <div className="error-message-fields">
                  Maximum monthly self amount is {monthlySelfDisplayMax} (
                  {annualSelfDisplayMax} per year)
                </div>
              )}
            </Stack>
          )}
        </Stack>

        {/* Age Allowance Table */}
        {values?.maxBenefit?.AGE && (
          <Fragment>
            <Box sx={{ marginTop: "24px" }}>
              <AgeAllowanceTable {...props} />
            </Box>
            {/* Shows its own age_qualify error once touched. */}
            <TextField
              formik={formik}
              type="hidden"
              name="age_qualify"
              label=""
            />
            <TextField
              formik={formik}
              type="hidden"
              name="ageAmountExceedsMax"
              label=""
            />
            {values.ageAmountExceedsMax === "No" && (
              <div className="error-message-fields">
                Maximum monthly self amount is {monthlySelfDisplayMax} (
                {annualSelfDisplayMax} per year)
              </div>
            )}
          </Fragment>
        )}

        {/* Family Age Allowance Table */}
        {values?.maxBenefit?.FAMILY_AGE && (
          <Fragment>
            <Box sx={{ marginTop: "24px" }}>
              <FamilyAllowanceTable {...props} />
            </Box>
            {/* Shows its own age_qualify error once touched. */}
            <TextField
              formik={formik}
              type="hidden"
              name="age_qualify"
              label=""
            />
            <TextField
              formik={formik}
              type="hidden"
              name="ageAmountExceedsMax"
              label=""
            />
            {values.ageAmountExceedsMax === "No" && (
              <div className="error-message-fields">
                Self amounts cannot exceed {monthlySelfDisplayMax} per month;
                amounts including dependents cannot exceed{" "}
                {monthlyFamilyDisplayMax} per month
              </div>
            )}
          </Fragment>
        )}

        <Stack spacing={2} sx={{ marginTop: "20px" }}>
          {/* Family amounts - three fields in a row */}
          {values?.maxBenefit?.FAMILY && (
            <Stack spacing="0px">
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: "1rem",
                  alignItems: "flex-end",
                }}
              >
                <Stack sx={{ width: { lg: "48%", xl: "31%" } }}>
                  <TextField
                    formik={formik}
                    type="number"
                    name="monthlySelfAmount"
                    label="Monthly Self Amount"
                    disabled={displayOnly}
                    validation="string"
                  />
                </Stack>

                <Stack sx={{ width: { lg: "48%", xl: "31%" } }}>
                  <TextField
                    formik={formik}
                    type="number"
                    name="monthlyFirstDependentAmount"
                    label="First Dependent Amount (Spouse or Child)"
                    placeHolder="0"
                    disabled={displayOnly}
                  />
                </Stack>

                <Stack sx={{ width: { lg: "48%", xl: "31%" } }}>
                  <TextField
                    formik={formik}
                    type="number"
                    name="monthlyAdditionalDependentAmount"
                    label="Each Additional Dependent Amount"
                    placeHolder="0"
                    disabled={displayOnly}
                  />
                </Stack>
              </Stack>

              {values.selfAmountExceedsMax === "No" && (
                <div className="error-message-fields">
                  Maximum monthly self amount is {monthlySelfDisplayMax} (
                  {annualSelfDisplayMax} per year)
                </div>
              )}
              {values.familyAmountExceedsMax === "No" && (
                <div className="error-message-fields">
                  Maximum monthly self + first dependent amount is{" "}
                  {monthlyFamilyDisplayMax} ({annualFamilyDisplayMax} per year)
                </div>
              )}
              {Number(values.monthlyAdditionalDependentAmount) > 0 &&
                monthlyFamilyAmountMax < Infinity && (
                  <Typography sx={{ fontSize: "14px", color: "#465578" }}>
                    Salusion caps the monthly allocation at{" "}
                    {monthlyFamilyDisplayMax} per month (
                    {annualFamilyDisplayMax} per year)
                  </Typography>
                )}
              {learnMoreHref && <AccrualNote href={learnMoreHref} />}
            </Stack>
          )}
        </Stack>
      </FormContainer>

      <NavButtons
        mode={mode}
        enableSave={true}
        exitUrl={exitUrl}
        step={step}
        onNextStep={handleNext}
      />
    </Stack>
  );

  // Render function for V1
  const renderV1 = () => (
    <Stack spacing="30px" height="100%">
      <FormContainer>
        <Typography variant="h2" sx={employerFlowStyles.h2}>
          Benefit
        </Typography>
        <Typography>Enter the monthly allowance</Typography>

        <Stack spacing={2} sx={{ marginTop: "20px" }}>
          {/* Radio buttons for max benefit type */}
          <CheckboxField
            formik={formik}
            type="radio"
            name="maxBenefit"
            options={inputOptions}
            displayKey="display"
            valueKey="value"
            disabled={displayOnly}
            showError={shouldShowErrors}
            validation={({ value }) => {
              const maxBenefit = value || {};
              const hasSelection = Object.values(maxBenefit).some(Boolean);
              if (!hasSelection) {
                return "Please select a benefit type";
              }
              return null;
            }}
          />
          <Field name="maxBenefit_required">
            {({ field }) => <input {...field} type="hidden" />}
          </Field>
          {errors.maxBenefit_required && shouldShowErrors && (
            <div className="error-message-fields">
              {errors.maxBenefit_required}
            </div>
          )}

          {/* Monthly Self Amount - shown when FAMILY is not selected */}
          {values?.maxBenefit?.FAMILY && (
            <TextField
              formik={formik}
              type="number"
              name="monthlySelfAmount"
              label={selfLabel}
              disabled={displayOnly}
              validation="string"
            />
          )}

          {/* SELF benefit type fields */}
          {values?.maxBenefit?.SELF && (
            <Fragment>
              <TextField
                formik={formik}
                type="number"
                name="selfAmount"
                label="Monthly Self Amount"
                disabled={displayOnly}
                validation="string"
              />

              <TextField
                formik={formik}
                type="number"
                name="spouseAmount"
                label="Self and Spouse Amount"
                disabled={displayOnly}
                validation="string"
              />

              <TextField
                formik={formik}
                type="number"
                name="childAmount"
                label="Self and Children Amount"
                disabled={displayOnly}
                validation="string"
              />

              <TextField
                formik={formik}
                type="number"
                name="allAmount"
                label="Self, Spouse and Children Amount"
                disabled={displayOnly}
                validation="string"
              />
            </Fragment>
          )}

          {/* Age Allowance Table for V1 */}
          {values?.maxBenefit?.AGE && (
            <Fragment>
              <AgeAllowanceTable {...props} />
              <TextField
                formik={formik}
                type="hidden"
                name="age_qualify"
                label=""
              />
              {errors.age_qualify && touched.age_qualify && (
                <div className="error-message-fields">
                  Amounts must stay the same or increase with age, and no
                  employee amount can be more than 3 times the age 21 amount.
                </div>
              )}
            </Fragment>
          )}

          {/* Family Age Allowance Table for V1 */}
          {values?.maxBenefit?.FAMILY_AGE && (
            <Fragment>
              <Box sx={{ marginTop: "24px" }}>
                <FamilyAllowanceTable {...props} />
              </Box>
              <TextField
                formik={formik}
                type="hidden"
                name="age_qualify"
                label=""
              />
              {errors.age_qualify && touched.age_qualify && (
                <div className="error-message-fields">
                  Amounts must stay the same or increase with age, and no
                  employee amount can be more than 3 times the age 21 amount.
                </div>
              )}
            </Fragment>
          )}
        </Stack>
      </FormContainer>

      <NavButtons
        mode={mode}
        exitUrl={exitUrl}
        step={step}
        onNextStep={handleNext}
      />
    </Stack>
  );

  // Main render logic
  if (v2) {
    return renderV2ManualEntry();
  } else {
    return renderV1();
  }
}
